// Shared methods mixin for all offer layout components
// This mixin contains common JavaScript logic that is identical across single, double, and triple layouts

export const offerMethodsMixin = {
  methods: {
    /**
     * Get currency symbol from currency code
     * @param {string} currencyCode - Currency code (EUR, USD, GBP)
     * @returns {string} Currency symbol
     */
    getCurrencySymbol(currencyCode) {
      const currencySymbols = {
        EUR: "€",
        USD: "$",
        GBP: "£",
      };
      return currencySymbols[currencyCode] || currencyCode;
    },

    /**
     * Format price with currency symbol
     * @param {Object} payPackage - Package object containing SKU and prices
     * @param {number|null} priceValue - Optional price override
     * @returns {string} Formatted price string
     */
    formatPrice(payPackage, priceValue = null) {
      if (!payPackage?.sku?.prices?.length) return "";

      const webPrice = payPackage.sku.prices.find((p) => p.platform === "web");
      if (!webPrice) return "";

      const price = priceValue !== null ? priceValue : webPrice.price;
      const n = Number(price);
      const symbol = this.getCurrencySymbol(webPrice.currency);

      if (!Number.isFinite(n)) return `${price}${symbol}`;

      const raw = this.$store?.state?.locale || "de";
      const locale = raw === "de" ? "de-DE" : "en-US";

      const formatted = new Intl.NumberFormat(locale, {
        useGrouping: false,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(n);

      return `${formatted}${symbol}`;
    },

    /**
     * Format old price from package payload
     * @param {Object} payPackage - Package object containing payload with old_price
     * @returns {string} Formatted old price string
     */
    formatOldPrice(payPackage) {
      if (!payPackage || !payPackage.payload || !payPackage.payload.old_price)
        return "";
      return this.formatPrice(payPackage, payPackage.payload.old_price);
    },

    /**
     * Format asset amount with locale-specific number formatting
     * @param {number} amount - Asset amount to format
     * @returns {string} Formatted amount string
     */
    formatAssetAmount(amount) {
      return amount.toLocaleString("de-DE");
    },

    /**
     * Whether a package is free (web price is 0) and should be claimed
     * rather than purchased through the paid payment flow.
     * @param {Object} payPackage - Package object
     * @returns {boolean}
     */
    isFreePackage(payPackage) {
      const prices = payPackage?.sku?.prices;
      if (!prices?.length) return false;
      const webPrice = prices.find((p) => p.platform === "web");
      return !!webPrice && Number(webPrice.price) === 0;
    },
    freePriceLabel() {
      return (this.$store?.state?.locale || "de") === "en" ? "Free" : "Gratis";
    },

    /**
     * Resolve a package's localized display name from its display_config.
     * `display_config.name` keys are `de` / `en` (not `de_DE`).
     * @param {Object} payPackage - Package object
     * @returns {string}
     */
    localizedPackageName(payPackage) {
      const localeKey =
        (this.$store?.state?.locale || "de") === "en" ? "en" : "de";
      let cfg = payPackage?.display_config;
      try {
        if (typeof cfg === "string") cfg = JSON.parse(cfg);
      } catch (e) {
        cfg = null;
      }
      const name = cfg && cfg.name;
      return (
        (name && (name[localeKey] || name.fallback)) || payPackage?.name || ""
      );
    },

    /**
     * Get CTA button text with price
     * @param {Object} payPackage - Package object
     * @returns {string} CTA button text
     */
    getCtaButtonText(payPackage) {
      if (!payPackage) return "";

      // Free package: label is the localized free price, no package name.
      if (this.isFreePackage(payPackage)) {
        return this.freePriceLabel();
      }

      // Use custom CTA text from displayConfig if available
      if (this.displayConfig?.cta_text) {
        const locale = this.$store.state.locale || "de_DE";
        const ctaText =
          this.displayConfig.cta_text[locale] ||
          this.displayConfig.cta_text.fallback ||
          "";
        return `${ctaText} ${this.formatPrice(payPackage)}`;
      }

      // Fallback to existing localization system
      const defaultText = this.$t("shop.offers.buy_now");
      return `${defaultText} ${this.formatPrice(payPackage)}`;
    },

    /**
     * Handle purchase event - emit to parent
     * @param {Object} payPackage - Package object to purchase
     */
    handlePurchase(payPackage) {
      this.$emit("purchase", payPackage);
    },
  },
};
